import { useState } from 'react';
import { Loader2, Lock, KeyRound } from 'lucide-react';
import { useVault } from '@/hooks/useVault';
import { OpenDorkWordmark } from './OpenDorkWordmark';

/**
 * Shown when a vault exists but is locked (passphrase vault, or NIP-44 failed).
 */
export function VaultUnlockGate({ children }: { children: React.ReactNode }) {
  const {
    status,
    error,
    supportsNip44,
    hasVault,
    unlockWithNip44,
    unlockWithPassphrase,
    clearError,
  } = useVault();

  const [passphrase, setPassphrase] = useState('');
  const [busy, setBusy] = useState(false);

  if (status === 'loading' || status === 'unlocking') {
    return (
      <div className="min-h-dvh bg-[#0a0a0a] text-[#5a5a5a] font-mono flex flex-col items-center justify-center gap-3">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-xs">
          {status === 'unlocking' ? 'unlocking vault…' : 'loading…'}
        </span>
      </div>
    );
  }

  if (status !== 'locked' || !hasVault) {
    return <>{children}</>;
  }

  const handleNip44 = async () => {
    clearError();
    setBusy(true);
    try {
      await unlockWithNip44();
    } catch {
      // error in context
    } finally {
      setBusy(false);
    }
  };

  const handlePassphrase = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setBusy(true);
    try {
      await unlockWithPassphrase(passphrase);
      setPassphrase('');
    } catch {
      // error in context
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-dvh bg-[#0a0a0a] text-[#e8e6e3] flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm flex flex-col items-center gap-6">
        <OpenDorkWordmark className="w-40 text-[#B7B1B1]" />
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#222] bg-[#111] text-[#8a8884]">
          <Lock className="w-3.5 h-3.5" />
          <span className="text-[12px] font-medium">Vault locked</span>
        </div>
        <p className="text-[13px] text-[#5a5a5a] text-center leading-relaxed max-w-xs">
          Your local workspace is encrypted at rest. Unlock with your Nostr
          signer or vault passphrase to continue.
        </p>

        {supportsNip44 && (
          <button
            type="button"
            onClick={() => void handleNip44()}
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-[13px] bg-[#e8e6e3] text-[#0a0a0a] rounded-xl font-medium hover:bg-white disabled:opacity-50 transition-colors"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <KeyRound className="w-4 h-4" />
            )}
            Unlock with Nostr signer
          </button>
        )}

        <form onSubmit={(e) => void handlePassphrase(e)} className="w-full space-y-2.5">
          {supportsNip44 && (
            <div className="flex items-center gap-3 text-[11px] text-[#3a3a3a]">
              <div className="flex-1 h-px bg-[#1a1a1a]" />
              <span>or passphrase</span>
              <div className="flex-1 h-px bg-[#1a1a1a]" />
            </div>
          )}
          <input
            type="password"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            placeholder="Vault passphrase"
            className="w-full bg-[#111] border border-[#2a2a2a] rounded-xl px-3.5 py-3 text-[13px] text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none focus:border-[#3a3a3a]"
            autoComplete="current-password"
          />
          <button
            type="submit"
            disabled={busy || passphrase.length < 8}
            className="w-full px-4 py-3 text-[13px] border border-[#2a2a2a] rounded-xl text-[#e8e6e3] hover:border-[#3a3a3a] hover:bg-[#141414] disabled:opacity-40 transition-colors"
          >
            Unlock with passphrase
          </button>
        </form>

        {error && (
          <p className="text-[12px] text-red-400 text-center leading-relaxed">{error}</p>
        )}
      </div>
    </div>
  );
}
